import { useTheme } from "@emotion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SIGNUP_FORM_CONTENT_CONTAINER_ID = "#signup-form-content-container";

// Threshold constants for different screen sizes
const THRESHOLDS = {
  PHONE: 25,
  MEDIUM: 50,
  LARGE: 100,
} as const;

// Hook for threshold-based header visibility with responsive thresholds
export const useHideNavbarOnScroll = (enabled = true) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isTargetScreen, setIsTargetScreen] = useState(false);
  const theme = useTheme();
  const rafIdRef = useRef<number>();
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const lastScrollTopRef = useRef(0);
  const containerRef = useRef<Element | null>(null);

  // Memoize media queries to prevent recreation
  const mediaQueries = useMemo(() => {
    return {
      phone:
        theme.breakpoints.SML?.replace("@media", "") || "(max-width: 767px)",
      medium: theme.breakpoints.MED.replace("@media", ""),
    };
  }, [theme.breakpoints.SML, theme.breakpoints.MED]);

  // Memoize MediaQueryList objects for better performance
  const mediaQueryLists = useMemo(() => {
    if (typeof window === "undefined") return null;

    return {
      phone: window.matchMedia(mediaQueries.phone),
      medium: window.matchMedia(mediaQueries.medium),
    };
  }, [mediaQueries.phone, mediaQueries.medium]);

  // Check if we're on target screen size (medium or below)
  const checkTargetScreen = useCallback(() => {
    if (!mediaQueryLists) return false;
    return mediaQueryLists.medium.matches;
  }, [mediaQueryLists]);

  // Memoized function to get current threshold based on screen size
  const getCurrentThreshold = useCallback(() => {
    if (!mediaQueryLists) return THRESHOLDS.LARGE;

    if (mediaQueryLists.phone.matches) {
      return THRESHOLDS.PHONE;
    } else if (mediaQueryLists.medium.matches) {
      return THRESHOLDS.MEDIUM;
    }
    return THRESHOLDS.LARGE;
  }, [mediaQueryLists]);

  // Optimized scroll handler with minimal computations
  const handleScroll = useCallback(() => {
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      if (!enabled || !isTargetScreen || !containerRef.current) {
        return;
      }

      const scrollTop = containerRef.current.scrollTop;

      // Only update if scroll position changed significantly (debounce micro-scrolls)
      if (Math.abs(scrollTop - lastScrollTopRef.current) < 2) {
        return;
      }

      lastScrollTopRef.current = scrollTop;
      const currentThreshold = getCurrentThreshold();

      // Simple threshold comparison
      setIsVisible(scrollTop < currentThreshold);
    });
  }, [enabled, isTargetScreen, getCurrentThreshold]);

  // Screen size change handler
  const handleScreenSizeChange = useCallback(() => {
    const newIsTargetScreen = checkTargetScreen();
    setIsTargetScreen(newIsTargetScreen);

    if (!newIsTargetScreen) {
      // If we're no longer on target screen, always show header
      setIsVisible(true);
    } else {
      // If we just entered target screen, recalculate
      handleScroll();
    }
  }, [checkTargetScreen, handleScroll]);

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      handleScreenSizeChange();
    }, 150); // Debounce resize events
  }, [handleScreenSizeChange]);

  // Effect to initialize and handle screen size changes
  useEffect(() => {
    if (!enabled || !mediaQueryLists) {
      setIsVisible(true);
      setIsTargetScreen(false);
      return;
    }

    // Initial screen size check
    handleScreenSizeChange();

    // Listen for screen size changes
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [enabled, mediaQueryLists, handleScreenSizeChange, handleResize]);

  // Effect to handle scroll events only when on target screen
  useEffect(() => {
    if (!enabled || !isTargetScreen) {
      return;
    }

    // Cache container reference to avoid repeated DOM queries
    const container = document.querySelector(SIGNUP_FORM_CONTENT_CONTAINER_ID);
    containerRef.current = container;

    if (!container) {
      return;
    }

    // Add scroll listener only when on target screen
    container.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    // Initial calculation
    handleScroll();

    return () => {
      // Cleanup scroll listener
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }

      // Clear refs
      containerRef.current = null;
    };
  }, [enabled, isTargetScreen, handleScroll]);

  return isVisible;
};
